import React from "react";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { Row, Col, Input } from "reactstrap";
import Select from "components/Select";
import ReactDatetime from "components/CustomDatePicker";
import {
  DEFAULT_SCHEDULE_REPORT,
  buildScheduleTimeOptions,
  getScheduleTimeOption,
} from "./reportScheduleMapper";
import { getReportSchedulePanelBoxStyle } from "./reportScheduleTheme";

const TIMEZONE_OPTIONS = [
  { value: "GMT-8", label: "(GMT -8) Pacific Time" },
  { value: "GMT-7", label: "(GMT -7) Mountain Time" },
  { value: "GMT-6", label: "(GMT -6) Central Time" },
  { value: "GMT-5", label: "(GMT -5) Eastern Time" },
  { value: "GMT+0", label: "(GMT +0) UTC" },
];

const DAY_OF_MONTH_OPTIONS = Array.from({ length: 31 }, (_, index) => {
  const day = String(index + 1);
  return { value: day, label: day };
});

const SCHEDULE_TIME_OPTIONS = buildScheduleTimeOptions();

const FrequencyOption = ({
  id,
  value,
  checked,
  onChange,
  bodyColor,
  children,
}) => (
  <div className="report-schedule-frequency-row">
    <input
      type="radio"
      id={id}
      name="schedule_frequency"
      className="report-schedule-frequency-radio"
      value={value}
      checked={checked}
      onChange={onChange}
    />
    <label
      htmlFor={id}
      className="report-schedule-frequency-label"
      style={{ color: bodyColor }}
    >
      {children}
    </label>
  </div>
);

const needsDateField = (frequency) =>
  ["one_time_only", "every_minutes", "every_hours", "weekly"].includes(frequency);

const needsDayOfMonthField = (frequency) => frequency === "monthly";

const needsTimeField = (frequency) =>
  !["every_minutes", "every_hours"].includes(frequency) || needsDateField(frequency);

const ReportScheduleReportSection = ({
  activeColor = "blue",
  colors,
  selectStyles,
  inputStyle,
  value,
  onChange,
}) => {
  const { t } = useTranslation();
  const scheduleData = value || DEFAULT_SCHEDULE_REPORT;
  const frequency = scheduleData.frequency || "one_time_only";
  const showDate = needsDateField(frequency);
  const showDayOfMonth = needsDayOfMonthField(frequency);
  const showTime = needsTimeField(frequency);

  const boxStyle = getReportSchedulePanelBoxStyle(colors, { marginTop: 8 });

  const inlineNumberStyle = {
    width: 60,
    display: "inline-block",
    margin: "0 8px",
    verticalAlign: "middle",
    ...inputStyle,
  };

  const updateScheduleData = (patch) => {
    onChange?.({ ...scheduleData, ...patch });
  };

  const handleFrequencyChange = (e) => {
    updateScheduleData({ frequency: e.target.value });
  };

  const handleFieldChange = (e) => {
    const { name, value: fieldValue } = e.target;
    updateScheduleData({ [name]: fieldValue });
  };

  const handleTimezoneChange = (option) => {
    updateScheduleData({ timezone: option?.value || "GMT-8" });
  };

  const handleDayOfMonthChange = (option) => {
    updateScheduleData({ day_of_month: option?.value || "1" });
  };

  const handleTimeChange = (option) => {
    updateScheduleData({ schedule_time: option?.value || "" });
  };

  return (
    <div className="report-schedule-report-section report-schedule-panel-box" style={boxStyle}>
      <h6
        className="mb-3 font-weight-bold report-schedule-panel-title"
        style={{ color: colors.titleColor }}
      >
        {t("reports.schedule.scheduleReportTitle")}
      </h6>

      <Row>
        <Col md="5">
          <FrequencyOption
            id="schedule_one_time_only"
            value="one_time_only"
            checked={frequency === "one_time_only"}
            onChange={handleFrequencyChange}
            bodyColor={colors.bodyColor}
          >
            {t("reports.schedule.oneTimeOnly")}
          </FrequencyOption>

          <FrequencyOption
            id="schedule_every_minutes"
            value="every_minutes"
            checked={frequency === "every_minutes"}
            onChange={handleFrequencyChange}
            bodyColor={colors.bodyColor}
          >
            {t("reports.schedule.every")}
            <Input
              type="number"
              name="custom_minutes"
              className="form-control"
              style={inlineNumberStyle}
              value={scheduleData.custom_minutes}
              onChange={handleFieldChange}
              min="1"
              max="59"
              disabled={frequency !== "every_minutes"}
            />
            {t("reports.schedule.minutes")}
          </FrequencyOption>

          <FrequencyOption
            id="schedule_every_hours"
            value="every_hours"
            checked={frequency === "every_hours"}
            onChange={handleFrequencyChange}
            bodyColor={colors.bodyColor}
          >
            {t("reports.schedule.every")}
            <Input
              type="number"
              name="custom_hours"
              className="form-control"
              style={inlineNumberStyle}
              value={scheduleData.custom_hours}
              onChange={handleFieldChange}
              min="1"
              max="23"
              disabled={frequency !== "every_hours"}
            />
            {t("reports.schedule.hours")}
          </FrequencyOption>

          <FrequencyOption
            id="schedule_daily"
            value="daily"
            checked={frequency === "daily"}
            onChange={handleFrequencyChange}
            bodyColor={colors.bodyColor}
          >
            {t("reports.schedule.daily")}
          </FrequencyOption>

          <FrequencyOption
            id="schedule_weekly"
            value="weekly"
            checked={frequency === "weekly"}
            onChange={handleFrequencyChange}
            bodyColor={colors.bodyColor}
          >
            {t("reports.schedule.weekly")}
          </FrequencyOption>

          <FrequencyOption
            id="schedule_monthly"
            value="monthly"
            checked={frequency === "monthly"}
            onChange={handleFrequencyChange}
            bodyColor={colors.bodyColor}
          >
            {t("reports.schedule.monthly")}
          </FrequencyOption>

          <FrequencyOption
            id="schedule_first_day_of_month"
            value="first_day_of_month"
            checked={frequency === "first_day_of_month"}
            onChange={handleFrequencyChange}
            bodyColor={colors.bodyColor}
          >
            {t("reports.schedule.firstDayOfMonth")}
          </FrequencyOption>

          <FrequencyOption
            id="schedule_last_day_of_month"
            value="last_day_of_month"
            checked={frequency === "last_day_of_month"}
            onChange={handleFrequencyChange}
            bodyColor={colors.bodyColor}
          >
            {t("reports.schedule.lastDayOfMonth")}
          </FrequencyOption>
        </Col>

        <Col md="7">
          {showDate && (
            <div className="mb-3">
              <div
                className="mb-2 font-weight-bold"
                style={{ color: colors.bodyColor, fontSize: 14 }}
              >
                {frequency === "weekly"
                  ? t("reports.schedule.referenceDate")
                  : t("reports.schedule.date")}
              </div>
              <ReactDatetime
                value={
                  scheduleData.schedule_date
                    ? moment(scheduleData.schedule_date, "YYYY-MM-DD")
                    : null
                }
                onChange={(date) =>
                  updateScheduleData({
                    schedule_date:
                      date && moment.isMoment(date)
                        ? date.format("YYYY-MM-DD")
                        : date instanceof Date
                          ? moment(date).format("YYYY-MM-DD")
                          : "",
                  })
                }
                dateFormat="YYYY-MM-DD"
                timeFormat={false}
                inputProps={{
                  placeholder: t("reports.schedule.selectDate"),
                  className: "form-control",
                  style: inputStyle,
                }}
              />
            </div>
          )}

          {showDayOfMonth && (
            <div className="mb-3">
              <div
                className="mb-2 font-weight-bold"
                style={{ color: colors.bodyColor, fontSize: 14 }}
              >
                {t("reports.schedule.dayOfMonth")}
              </div>
              <Select
                activeColor={activeColor}
                styles={selectStyles}
                className="react-select"
                placeholder={t("reports.schedule.selectDayOfMonth")}
                isSearchable={false}
                closeMenuOnSelect
                value={
                  DAY_OF_MONTH_OPTIONS.find(
                    (opt) => opt.value === String(scheduleData.day_of_month || "1")
                  ) || DAY_OF_MONTH_OPTIONS[0]
                }
                onChange={handleDayOfMonthChange}
                options={DAY_OF_MONTH_OPTIONS}
              />
            </div>
          )}

          {(showTime || frequency === "every_minutes" || frequency === "every_hours") && (
            <div>
              <div
                className="mb-2 font-weight-bold"
                style={{ color: colors.bodyColor, fontSize: 14 }}
              >
                {t("reports.schedule.time")}
              </div>
              <div className="d-flex align-items-center flex-wrap" style={{ gap: 8 }}>
                <div style={{ width: 140, flexShrink: 0 }}>
                  <Select
                    activeColor={activeColor}
                    styles={selectStyles}
                    className="react-select"
                    placeholder={t("reports.schedule.selectTime")}
                    isSearchable
                    closeMenuOnSelect
                    value={getScheduleTimeOption(scheduleData.schedule_time)}
                    onChange={handleTimeChange}
                    options={SCHEDULE_TIME_OPTIONS}
                  />
                </div>
                <div style={{ flex: 1, minWidth: 220 }}>
                  <Select
                    activeColor={activeColor}
                    styles={selectStyles}
                    className="react-select"
                    placeholder={t("reports.schedule.selectTimezone")}
                    isSearchable={false}
                    closeMenuOnSelect
                    value={
                      scheduleData.timezone
                        ? TIMEZONE_OPTIONS.find((opt) => opt.value === scheduleData.timezone) || {
                            value: scheduleData.timezone,
                            label: scheduleData.timezone,
                          }
                        : null
                    }
                    onChange={handleTimezoneChange}
                    options={TIMEZONE_OPTIONS}
                  />
                </div>
              </div>
            </div>
          )}
        </Col>
      </Row>
    </div>
  );
};

export default ReportScheduleReportSection;
